import { fetchWithAuth } from './http-client'
import { parseStandardApiResponse } from './standard-api'

// Route through API Gateway base to avoid coupling with /users path
// Using versioned API endpoints (v1) for proper data transformations
// Examples: VITE_API_URL=http://localhost:3101/api/v1 → API_BASE=/api/v1
const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '')
const API_ENV_VAR = 'VITE_API_URL'

// Backend schema (snake_case)
interface TagBackend {
  id: string
  code: string
  name: string
  description?: string
  color_code?: string
  icon?: string
  usage_count: number
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

// Frontend interface (camelCase)
export interface Tag {
  id: string
  code: string
  name: string
  description?: string
  icon?: string
  color?: string
  isActive: boolean
  usageCount: number
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface CreateTagDto {
  code?: string  // Optional - backend auto-generates from UUID if not provided
  name: string
  description?: string
  icon?: string
  color?: string
  isActive?: boolean
}

export interface UpdateTagDto {
  code?: string
  name?: string
  description?: string
  icon?: string
  color?: string
  isActive?: boolean
}

export interface TagsListParams {
  page?: number
  pageSize?: number
  search?: string
  status?: 'all' | 'active' | 'inactive'
}

export interface TagsListResponse {
  data: Tag[]
  total: number
  page: number
  pageSize: number
}

// Transform backend tag to frontend format
function transformTag(backendTag: TagBackend): Tag {
  return {
    id: backendTag.id,
    code: backendTag.code,
    name: backendTag.name,
    description: backendTag.description,
    icon: backendTag.icon,
    color: backendTag.color_code || undefined,
    isActive: backendTag.is_active,
    usageCount: backendTag.usage_count,
    sortOrder: backendTag.sort_order,
    createdAt: backendTag.created_at,
    updatedAt: backendTag.updated_at,
  }
}

// Transform frontend DTO to backend format
function transformCreateDto(dto: CreateTagDto) {
  const payload: Record<string, any> = {
    name: dto.name,
    description: dto.description,
    icon: dto.icon,
    color_code: dto.color,
    is_active: dto.isActive,
  }

  // Only include code if provided (backend auto-generates if not)
  if (dto.code) {
    payload.code = dto.code
  }

  return payload
}

function transformUpdateDto(dto: UpdateTagDto) {
  return {
    code: dto.code,
    name: dto.name,
    description: dto.description,
    icon: dto.icon,
    color_code: dto.color,
    is_active: dto.isActive,
  }
}

export async function getTags(params: TagsListParams = {}): Promise<TagsListResponse> {
  const searchParams = new URLSearchParams()
  if (params.page) searchParams.set('page', params.page.toString())
  if (params.pageSize) searchParams.set('limit', params.pageSize.toString()) // Backend uses 'limit'
  if (params.search) searchParams.set('search', params.search)
  
  // Backend uses is_active boolean instead of status string
  if (params.status === 'active') searchParams.set('is_active', 'true')
  if (params.status === 'inactive') searchParams.set('is_active', 'false')

  const response = await fetchWithAuth(`${API_BASE}/tags?${searchParams.toString()}`, {
    context: 'Listado de tags',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  const { data, meta } = await parseStandardApiResponse<TagBackend[]>(
    response,
    'Listado de tags'
  )

  const paginationMeta = meta.pagination ?? {
    page: Number(params.page ?? 1),
    limit: Number(params.pageSize ?? (data.length || 1)),
    total: data.length,
    totalPages: data.length && params.pageSize ? Math.ceil(data.length / params.pageSize) : 1
  }
  
  return {
    data: data.map(transformTag),
    total: paginationMeta.total,
    page: paginationMeta.page,
    pageSize: paginationMeta.limit,
  }
}

export async function getTagById(id: string): Promise<Tag> {
  const response = await fetchWithAuth(`${API_BASE}/tags/${id}`, {
    context: 'Detalle de tag',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  const { data } = await parseStandardApiResponse<TagBackend>(
    response,
    'Detalle de tag'
  )
  return transformTag(data)
}

export async function createTag(data: CreateTagDto): Promise<Tag> {
  const response = await fetchWithAuth(`${API_BASE}/tags`, {
    method: 'POST',
    body: JSON.stringify(transformCreateDto(data)),
    context: 'Creación de tag',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  const { data: backendTag } = await parseStandardApiResponse<TagBackend>(
    response,
    'Creación de tag'
  )
  return transformTag(backendTag)
}

export async function updateTag(id: string, data: UpdateTagDto): Promise<Tag> {
  const response = await fetchWithAuth(`${API_BASE}/tags/${id}`, {
    method: 'PUT',
    body: JSON.stringify(transformUpdateDto(data)),
    context: 'Actualización de tag',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  const { data: backendTag } = await parseStandardApiResponse<TagBackend>(
    response,
    'Actualización de tag'
  )
  return transformTag(backendTag)
}

export async function deleteTag(id: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/tags/${id}`, {
    method: 'DELETE',
    context: 'Eliminación de tag',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  await parseStandardApiResponse<unknown>(response, 'Eliminación de tag')
}

/**
 * PROJ-038: Bulk delete multiple tags at once
 */
export interface BulkDeleteResponse {
  deleted: number
  failed: string[]
  total: number
}

export async function bulkDeleteTags(ids: string[]): Promise<BulkDeleteResponse> {
  const response = await fetchWithAuth(`${API_BASE}/tags/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
    context: 'Eliminación masiva de tags',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  })
  const { data } = await parseStandardApiResponse<BulkDeleteResponse>(
    response,
    'Eliminación masiva de tags'
  )
  return data
}
